import React, { useState, ChangeEvent, useRef, useEffect, useMemo } from "react";

import { Menu, MenuItem, Typography, Box, Button, CircularProgress } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Job } from "Models/job";
import { useTranslation } from "react-i18next";
import { getMuiPopupContainer } from "Themes/dispatchTheme";

import { Input } from "../../../../../../components/Form";
import { useStyles2 as useStyles } from "../../../Diary/views/DiaryResourcesView/style";
import { CreateTaskOrderOption } from "./OrderOption";
import { Label } from "./styles";

const FIND = require("Assets/icons/find.svg");

interface MainComponentProps {
  onItemSelect: (option: Job) => void;
  options: Job[];
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filterQuery: string;
  setFilterQuery: (value: string) => void;
  isLoading: boolean;
  filters: Array<{ label: string; searchFor: string }>;
  boxWidth?: string;
}

export const MainComponent: React.FC<MainComponentProps> = ({
  onItemSelect,
  options,
  searchQuery,
  setSearchQuery,
  isLoading: loading,
  filters,
  filterQuery,
  setFilterQuery,
  boxWidth = "400px",
}) => {
  const [openBox, setOpenBox] = useState(true);
  const [newDropdownOpen, setNewDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const classes = useStyles();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const btndropdownRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t: translate } = useTranslation();

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [isEnterPressed, setIsEnterPressed] = useState(false);

  const handleBlur = () => {
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setOpenBox(false);
      }
    }, 100);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalSearchQuery(event.target.value);
    setOpenBox(true);
  };

  const handleFocus = () => {
    setOpenBox(true);
  };

  useEffect(() => {
    if (!openBox && inputRef.current) {
      setIsEnterPressed(false);
      inputRef.current.blur();
    }
  }, [openBox]);

  const handleCardClick = (option: Job) => {
    onItemSelect(option);
    setOpenBox(false);
  };

  const handleClear = () => {
    setLocalSearchQuery("");
    setSearchQuery("");
    setOpenBox(false);
    setIsEnterPressed(false);
  };

  const handleNewDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setNewDropdownOpen((prev) => !prev);
  };

  const handleNewOptionSelect = (option: string) => {
    setFilterQuery(option);
    setNewDropdownOpen(false);
    setAnchorEl(null);
  };

  const handleMouseDown = (event: { preventDefault: () => void }) => {
    event.preventDefault();
  };

  const filterLabelSelected = useMemo(
    () => filters.find((filter) => filter.searchFor === filterQuery)?.label,
    [filters, filterQuery]
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Box className={classes.loadNoData}>
          <CircularProgress size={24} />
          <Typography className={classes.cardName}>{translate("loading")}...</Typography>
        </Box>
      );
    }
    if (options.length > 0) {
      return (
        <>
          {options.map((item) => (
            <div
              key={item.id}
              className={classes.jobCardClickable}
              onClick={() => handleCardClick(item)}
            >
              <CreateTaskOrderOption data={item} />
            </div>
          ))}
        </>
      );
    }

    if (!isEnterPressed) {
      return null;
    }

    return (
      <Box className={classes.loadNoData}>
        <Typography className={classes.cardName}>{translate("noResultMatchYourSearch")}</Typography>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" rowGap="4px">
      <Label>{translate("searchOrderBy")}</Label>
      <Box width={boxWidth} className={classes.Fortarget}>
        <Button
          ref={btndropdownRef}
          style={{ height: "40px" }}
          className={classes.reference}
          endIcon={newDropdownOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          onClick={handleNewDropdownClick}
        >
          {filterLabelSelected}
        </Button>

        {openBox && (
          <Box className={classes.menulistscrollbox} onMouseDown={handleMouseDown}>
            {renderContent()}
          </Box>
        )}

        <Box sx={{ flexGrow: 1, flex: 1 }}>
          <Input
            inputRef={inputRef}
            fullWidth
            placeholder={`${translate("typeHereAndPressEnter")}...`}
            value={localSearchQuery}
            onChange={handleSearchChange}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Enter") {
                setIsEnterPressed(true);
                setSearchQuery(localSearchQuery);
              }
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            rightIcon={
              localSearchQuery ? (
                <ClearIcon onClick={handleClear} style={{ cursor: "pointer" }} />
              ) : (
                <img src={FIND} alt="Find" />
              )
            }
          />
        </Box>
        <Menu
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          open={newDropdownOpen}
          anchorEl={anchorEl}
          onClose={() => setNewDropdownOpen(false)}
          PaperProps={{
            style: {
              marginTop: "100px",
              maxHeight: "200px",
            },
          }}
          container={getMuiPopupContainer}
        >
          <Box>
            {filters.map((option) => (
              <MenuItem
                className={classes.btnmenulist}
                key={option.searchFor}
                onClick={() => handleNewOptionSelect(option.searchFor)}
              >
                <Typography>{option.label}</Typography>
              </MenuItem>
            ))}
          </Box>
        </Menu>
      </Box>
    </Box>
  );
};
